import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import walletEuroBlackIcon from 'assets/svg/icons/wallet-euro-black-icon.svg';
import classNames from 'classnames';
import dayjs from 'dayjs';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import useModal from 'hooks/useModal';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Connector } from 'types/transactions.types';
import { getTracingEvents } from 'utils/amplitude';
import { WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER } from 'utils/amplitude/events/bank.amplitude';
import showToast from 'utils/toast';
import { getTaxSavingsAccountableBank } from '../helpers';
import useTotalTaxesToSetAside from '../useTotalTaxesToSetAside';
import { WalletSetAsideToastWrapper } from './WalletSetAsideToast.component';
import useFeatureLock from 'hooks/Subscriptions/useFeatureLock';
import { SubscriptionLockKeysEnum } from 'types/subscriptions.types';

const tracingEvents = getTracingEvents({
  onTransferSuccess: WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER,
});

const useWalletSetAsideToast = () => {
  const { t } = useTranslation();

  const [modalContent, modalApi] = useModal({
    width: '2xl',
    name: 'Wallet set aside toast',
    color: 'bank',
  });

  const { isFeatureLocked } = useFeatureLock();

  const { totalTaxesToSetAside } = useTotalTaxesToSetAside();

  const { data: connectors } = useGetConnectors();

  const taxSavingsAccountAmount =
    getTaxSavingsAccountableBank(connectors as Connector[])?.balance?.amount ||
    0;

  useEffect(() => {
    const walletNudgesData = JSON.parse(
      localStorage.getItem('walletNudgesData') || '{}',
    );

    const isANewDay = dayjs(walletNudgesData.day).isBefore(dayjs(), 'day');

    if (isANewDay) {
      localStorage.setItem(
        'walletNudgesData',
        JSON.stringify({ day: dayjs(), openedToday: false }),
      );
    }
  }, []);

  const canAccessAccountableBanking = useCanAccessAccountableBanking();

  const showWalletSetAsideToast = (type?: 'toast' | 'modal') => {
    const walletNudgesData = JSON.parse(
      localStorage.getItem('walletNudgesData') || '{}',
    );

    /* ACCOUNTABLE BANKING: BETA TESTING CONDITION */
    if (
      taxSavingsAccountAmount > totalTaxesToSetAside ||
      walletNudgesData.openedToday ||
      !canAccessAccountableBanking ||
      isFeatureLocked(SubscriptionLockKeysEnum.viewWebBankingPage)
    )
      return;

    localStorage.setItem(
      'walletNudgesData',
      JSON.stringify({ day: dayjs(), openedToday: true }),
    );

    if (type === 'modal') {
      return modalApi.open(
        () => (
          <WalletSetAsideToastWrapper
            onTransferSuccess={() => {
              tracingEvents.onTransferSuccess();

              showToast(
                {
                  title: t('accountable_banking.successful_payment_msg'),
                },
                { type: 'success' },
              );
              modalApi.close();
            }}
          />
        ),
        {
          handleInitFocus: false,
          name: 'Wallet set aside toast',
        },
      );
    }

    showToast(
      {
        title: t('accountable_banking.tax_reserve.top_up_reminder'),
        icon: <img src={walletEuroBlackIcon} />,
        actionButtons: [
          {
            title: (
              <QuestionMarkCircleIcon
                className={classNames(
                  'w-5 text-primary-300 hover:text-primary-400 focus:text-primary-400',
                )}
              />
            ),
            onClick: () =>
              modalApi.open(
                () => (
                  <WalletSetAsideToastWrapper
                    onTransferSuccess={() => {
                      tracingEvents.onTransferSuccess();

                      showToast(
                        {
                          title: t(
                            'accountable_banking.successful_payment_msg',
                          ),
                        },
                        { type: 'success' },
                      );
                      modalApi.close();
                    }}
                  />
                ),
                {
                  handleInitFocus: false,
                  name: 'Wallet set aside toast',
                },
              ),
            asDefault: true,
          },
        ],
      },
      {
        position: 'top-center',
      },
    );
  };

  return {
    walletSetAsideToastContent: modalContent,
    showWalletSetAsideToast,
  };
};

export default useWalletSetAsideToast;
